import React from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  ExportOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Result,
  Row,
  Spin,
  Table,
} from "antd";
import moment from "moment";
import { Util } from "common/util";
import { getOvertimeReport } from "services/overtime_report";
import { Approved, Rejected, Pending, Reviewed } from "common/status";
import Enum from "common/enums";

const { RangePicker } = DatePicker;

export default function OvertimeReport() {
  const [datas, setDatas] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();
  const pathName = "/reports/overtime";
  const params = new URLSearchParams(document.location.search);
  let timer = null;
  let rowKeyIndex = 1;

  React.useMemo(() => {
    const params = new URLSearchParams(document.location.search);
    if (!params.get("date"))
      params.set(
        "date",
        `${moment().format("YYYY-MM")},${moment().format("YYYY-MM")}`
      );
    Util.getInstance().pushParamsToURL(pathName, params.toString());
  }, []);

  React.useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, []);

  const fetchList = () => {
    const params = new URLSearchParams(document.location.search);
    let option = {};
    if (params.has("date")) {
      const rangeDates = params.get("date").split(",");
      option["date"] = JSON.stringify({
        value: rangeDates,
      });
    }
    if (params.has("search")) option["search"] = params.get("search");
    setLoading(true);
    getOvertimeReport(option)
      .then((response) => {
        if (response) {
          setDatas(response);
        }
      })
      .catch((error) => {
        if (error.response.status === 403) {
          localStorage.removeItem("ACCESS_TOKEN");
          navigate("/signin");
        }
      })
      .finally(() => setLoading(false));
  };

  const onSearch = (e) => {
    clearTimeout(timer);
    const value = e.target.value;
    const params = new URLSearchParams(document.location.search);

    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }

    Util.getInstance().pushParamsToURL(pathName, params.toString());
    timer = setTimeout(() => {
      fetchList();
    }, 1000);
  };

  const onChangeDate = (values) => {
    const queryParams = new URLSearchParams(document.location.search);
    if (values) {
      const dates = `${moment(values[0]).format("YYYY-MM")},${moment(
        values[1]
      ).format("YYYY-MM")}`;
      queryParams.set("date", dates);
    } else {
      queryParams.delete("date");
    }
    Util.getInstance().pushParamsToURL(
      pathName,
      queryParams.toString()
    );
    fetchList();
  };

  if (!Util.getInstance().hasPermission("view", "overtime report")) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("text_sorry_you_are_not_authorized_to_access_this_page")}
      />
    );
  }
  return (
    <>
      <Row style={styles.breadCream}>
        <Col span={24}>
          <Breadcrumb>
            <Breadcrumb.Item
              onClick={() => navigate("/")}
              style={{ cursor: "pointer" }}
            >
              {t("text_dashboard")}
            </Breadcrumb.Item>
            <Breadcrumb.Item>{t("text_overtime_report")}</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>
      <Row style={{ padding: 15 }} id="header-report-table">
        <Col span={12}>
          <Title subTitle={t("text_overtime_report")} />
        </Col>
        <Col
          span={12}
          style={{
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            paddingRight: 5,
          }}
        >
          <Input
            placeholder={t("text_search_by_employee")}
            defaultValue={params.get("search")}
            prefix={<SearchOutlined />}
            style={styles.generalSearch}
            allowClear={true}
            onChange={onSearch}
          />
          <RangePicker
            name="dates"
            placeholder={["MM/YYYY", "MM/YYYY"]}
            format={["MM/YYYY", "MM/YYYY"]}
            value={
              params.get("date")
                ? [
                    moment(params.get("date").split(",")[0]),
                    moment(params.get("date").split(",")[1]),
                  ]
                : []
            }
            onChange={onChangeDate}
            allowClear={false}
            style={{
              width: "330px",
              height: "32px",
              marginLeft: "8px",
            }}
            picker="month"
            separator={"~"}
          />
          <ExportButton />
        </Col>
      </Row>

      <div id="report-table">
        <Table
          bordered
          dataSource={datas}
          pagination={false}
           scroll={
              datas ? {
                y: window.innerHeight - 280,
              } : undefined
            }
          rowKey={() => `${rowKeyIndex++}`}
          columns={[
            {
              title: "Name",
              dataIndex: "employeeName",
              key: "employeeName",
            },
            {
              title: "EMP#",
              dataIndex: "employeeCode",
              key: "employeeCode",
            },
            {
              title: "Position",
              dataIndex: "position",
              key: "position",
            },
            {
              title: "Start Date",
              dataIndex: "startDate",
              key: "startDate",
              render: (startDate) =>
                startDate && moment(startDate).format("DD/MM/YYYY"),
            },
            {
              title: "End Date",
              dataIndex: "endDate",
              key: "endDate",
              render: (endDate) =>
                endDate && moment(endDate).format("DD/MM/YYYY"),
            },
            {
              title: "Number of Day",
              dataIndex: "numberOfDay",
              key: "numberOfDay",
            },
            {
              title: "Reason",
              dataIndex: "reason",
              key: "reason",
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (status) => {
                if (status === Enum.LEAVESTATUS.APPROVED) {
                  return <Approved />;
                } else if (status === Enum.LEAVESTATUS.REJECTED) {
                  return <Rejected />;
                } else if (status === Enum.LEAVESTATUS.REQUESTED) {
                  return <Pending />;
                } else if (status === Enum.LEAVESTATUS.REVIEWED) {
                  return <Reviewed />;
                }
              },
            },
          ]}
          loading={loading}
        />
      </div>
    </>
  );
}

function Title({ subTitle }) {
  return <span id="title-table-report">{subTitle}</span>;
}

function ExportButton() {
  const [visible, setVisible] = React.useState(false);

  const onClose = () => {
    setVisible(false);
  };

  const onShow = () => {
    setVisible(true);
  };

  return (
    <>
      <Button
        icon={<ExportOutlined />}
        onClick={onShow}
        style={{ marginLeft: 10 }}
      >
        {t("text_export")}
      </Button>
      <Drawer
        title={
          <div style={styles.headerFilter}>
            <div>{t("text_export_report")}</div>
            <div>
              <CloseOutlined onClick={onClose} />
            </div>
          </div>
        }
        placement="right"
        closable={false}
        onClose={onClose}
        visible={visible}
      >
        {visible ? <ExportExcelFile /> : null}
      </Drawer>
    </>
  );
}

const ExportExcelFile = () => {
  const [loading, setLoading] = React.useState(true);
  const [result, setResult] = React.useState(null);
  const navigate = useNavigate();
  const params = new URLSearchParams(document.location.search);

  React.useEffect(() => {
    let option = {};
    if (params.get("date")) {
      const rangeDates = params.get("date").split(",");
      option["date"] = JSON.stringify({
        value: rangeDates,
      });
    }
    if (params.has("search")) option["search"] = params.get("search");

    option["isExport"] = true;
    getOvertimeReport(option)
      .then((response) => {
        if (response) {
          setResult(response);
        }
      })
      .catch((error) => {
        if (error.response.status === 403) {
          localStorage.removeItem("ACCESS_TOKEN");
          navigate("/signin");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  return (
    <>
      {loading ? (
        <Spin
          spinning={loading}
          style={{
            height: "24vh",
            display: "flex",
            alignItems: "center",
          }}
        ></Spin>
      ) : (
        <Result
          status="success"
          title={"Successfully exported Overtime Report"}
          extra={[
            <div style={{ marginBottom: 25 }}>
              <a
                key="1"
                className="ant-btn ant-btn-dashed"
                href={result ? result.link : "#"}
              >
                {t("text_download_file_xlsx")}
              </a>
            </div>,
          ]}
        />
      )}
    </>
  );
};

const DatePickerFilter = ({
  pathName,
  fetchList,
  dateFormat,
  datePlaceholder,
  pickerDate,
}) => {
  const queryParams = new URLSearchParams(document.location.search);
  const onChangeDate = (value, valueString) => {
    const dateString = valueString.split("/");
    if (value) {
      if (dateString.length === 3) {
        queryParams.set("date", Util.getInstance().formatDateForMYSQL(value));
      } else {
        queryParams.set("date", moment(value).format("YYYY-MM"));
      }
    } else {
      queryParams.delete("date");
    }
    Util.getInstance().pushParamsToURL(pathName, queryParams.toString());
    fetchList();
  };

  return (
    <>
      <Form style={{ marginLeft: "8px" }}>
        <DatePicker
          name="date"
          placeholder={`${datePlaceholder}`}
          format={`${dateFormat}`}
          style={{ marginLeft: "8px", width: "180px" }}
          defaultValue={
            queryParams.get("date") && moment(queryParams.get("date"))
          }
          picker={`${pickerDate}`}
          allowClear={false}
          onChange={onChangeDate}
        />
      </Form>
    </>
  );
};

const styles = {
  formInline: {
    width: 203,
    display: "inline-block",
    textAlign: "left",
    marginRight: 5,
    marginBottom: 0,
    marginTop: 0,
  },
  generalSearch: {
    width: 203,
  },
  headerFilter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breadCream: {
    paddingBottom: 15,
    backgroundColor: "#EFF3F6",
  },
};
